import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { Company } from '@/models/company';
import {
  Box,
  Paper,
  Stack,
  CircularProgress,
  Button,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  CreateNewFolder,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Business as BusinessIcon,
  Search,
  Description as DescriptionIcon,
  ArrowDropDown,
} from '@mui/icons-material';
import Cookies from 'js-cookie';
import { useQueryClient } from '@tanstack/react-query';
import { useCompanyStore } from '@/stores/company-store';
import { getToken, useSnackbarStore } from '@certara/common-react-components';
import { useTranslation } from 'react-i18next';

import { useQueue } from '@/hooks/use-queue';
import { useGridData } from '@/hooks/use-grid-data';
import { queueService } from '@/services/queue-service';
import { useUIStore } from '@/stores/ui-store';
import { CreateFolder } from '@/components/Storage/CreateFolder/CreateFolder';
import { Upload } from '@/components/Storage/Upload/Upload';
import { Delete } from '@/components/Storage/Delete/Delete';
import { Rename } from '@/components/Storage/Rename/Rename';
import { LoadControls } from '@/components/Controls/LoadControls';
import { BatchRenameDialog } from '@/components/Storage/Rename/BatchRenameDialog';
import { RepoGrid } from '@/components/Grid/RepoGrid';
import {
  ActivityDialog,
  ConfigurationDialog,
  ReportDialog,
} from '@/components/Dialogs';
import { useNavigationStore } from '@/stores/navigation-store';
// import type { GridItem } from '@/services/grid-service'; // TODO: Re-enable for preview

// Mock companies for company selector
const mockCompanies = [
  { Id: 1, Name: 'Acme Pharmaceuticals', Expiration: null },
  { Id: 2, Name: 'GlobalBio Corp', Expiration: null },
  { Id: 3, Name: 'MedTech Solutions', Expiration: null },
];


function MainPage() {
  const { t } = useTranslation('load');
  const { activeCompany, setActiveCompany } = useCompanyStore();

  // Company menu state
  const [companyMenuAnchor, setCompanyMenuAnchor] = useState<null | HTMLElement>(null);
  const companyMenuOpen = Boolean(companyMenuAnchor);

  // Search state
  const [repoSearch, setRepoSearch] = useState('');

  // Storage dialogs - local state
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [batchRenameOpen, setBatchRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // System dialogs - Zustand-based (manage themselves)
  const setActivityDialogOpen = useUIStore((state) => state.setActivityDialogOpen);

  // File Actions dropdown
  const [fileActionsAnchor, setFileActionsAnchor] = useState<null | HTMLElement>(null);
  const fileActionsOpen = Boolean(fileActionsAnchor);

  // Report dialogs
  const [submissionReportOpen, setSubmissionReportOpen] = useState(false);
  const [applicationReportOpen, setApplicationReportOpen] = useState(false);

  const currentPath = useNavigationStore((state) => state.currentPath);
  const rawSelectedItems = useNavigationStore((state) => state.selectedItems);
  const selectedItems = useMemo(() => rawSelectedItems || [], [rawSelectedItems]);
  const breadcrumbs = useNavigationStore((state) => state.breadcrumbs);
  const navigateToBreadcrumb = useNavigationStore((state) => state.navigateToBreadcrumb);

  const queryClient = useQueryClient();

  // Get queue data first to know if there are active operations
  const { data: queueData = [] } = useQueue(activeCompany?.Id, true, activeCompany?.Id === 1);

  const hasActiveOperations = queueData.some(
    (item) => item.Status === 'Processing' || item.Status === 'Pending' || item.Status === 'Blocked'
  );

  const pendingCount =
    queueData?.filter(
      (item) => item.Status === 'Pending' || item.Status === 'Processing' || item.Status === 'Blocked'
    ).length || 0;
  const failedCount =
    queueData?.filter((item) => item.Status === 'Failed').length || 0;
  const completedCount =
    queueData?.filter((item) => item.Status === 'Completed').length || 0;

  const activityTooltipLines = useMemo(() => {
    const lines: string[] = [];
    if (pendingCount > 0) lines.push(t('pages.main.tooltipProcessing', { count: pendingCount }));
    if (completedCount > 0) lines.push(t('pages.main.tooltipCompleted', { count: completedCount }));
    if (failedCount > 0) lines.push(t('pages.main.tooltipFailed', { count: failedCount }));
    return lines.length > 0 ? lines.join('\n') : t('pages.main.tooltipNoActivity');
  }, [pendingCount, completedCount, failedCount, t]);

  // Load grid data for current company and path
  // Auto-refreshes every 3s when there are active operations
  useGridData(activeCompany?.Id, currentPath, hasActiveOperations);

  // Track previous queue items to detect when operations complete
  const prevQueueItemsRef = useRef<typeof queueData>([]);

  // Refresh grid when processing operations complete (matching Vue's queueData watcher)
  useEffect(() => {
    const prevItems = prevQueueItemsRef.current;
    
    // Find items that were processing/pending but are no longer in queue or changed to Completed
    const prevActive = prevItems.filter(
      (item) => item.Status === 'Processing' || item.Status === 'Pending' || item.Status === 'Blocked'
    );
    
    const currentActiveIds = new Set(
      queueData
        .filter((item) => item.Status === 'Processing' || item.Status === 'Pending' || item.Status === 'Blocked')
        .map((item) => item.QueueId)
    );
    
    // Find items that completed (were active before, not active now)
    const completedItems = prevActive.filter((item) => !currentActiveIds.has(item.QueueId));
    
    if (completedItems.length > 0 && activeCompany) {
      // Call UpdateCompletedOperations API like Vue does, then refresh grid
      queueService.updateCompletedOperations(activeCompany.Id, completedItems)
        .then(() => {
          // Refresh grid data after backend acknowledges completion
          queryClient.refetchQueries({ queryKey: ['gridData', activeCompany.Id, currentPath] });
        })
        .catch(() => {
          // Still refresh grid even if updateCompletedOperations fails
          queryClient.refetchQueries({ queryKey: ['gridData', activeCompany.Id, currentPath] });
        });
    }
    
    // Update ref for next comparison
    prevQueueItemsRef.current = queueData;
  }, [queueData, activeCompany, currentPath, queryClient]);

  const hasSelection = selectedItems.length > 0;

  // Report button visibility - single folder selected with appropriate loaded state
  const singleSelectedFolder = selectedItems.length === 1 && selectedItems[0].IsFolder
    ? selectedItems[0]
    : null;

  const showSubmissionReport = Boolean(
    singleSelectedFolder?.IsLoadedSub
  );

  const showApplicationReport = Boolean(
    singleSelectedFolder?.IsLoadedApp && !singleSelectedFolder?.IsSub
  );

  // Build report download URLs
  const submissionReportUrl = useMemo(() => {
    if (!activeCompany || !singleSelectedFolder) return '';
    return `api/Reports/DownloadSubmissionReport?companyId=${activeCompany.Id}&appId=${singleSelectedFolder.AppId}&subId=${singleSelectedFolder.SubId}&profileId=0`;
  }, [activeCompany, singleSelectedFolder]);

  const applicationReportUrl = useMemo(() => {
    if (!activeCompany || !singleSelectedFolder) return '';
    return `api/Reports/DownloadApplicationReport?companyId=${activeCompany.Id}&appId=${singleSelectedFolder.AppId}&profileId=0`;
  }, [activeCompany, singleSelectedFolder]);

  const { addSnackbar } = useSnackbarStore();

  // Construct current folder path for download (matching Vue's currentLocationPath getter)
  // Vue's currentLocationPath is just path parts joined by '/' without leading slash
  const getCurrentFolderPath = useCallback(() => {
    return currentPath || '';
  }, [currentPath]);

  // Download handler - matching Vue's Download.vue implementation
  const handleDownload = useCallback(async () => {
    if (!activeCompany || selectedItems.length === 0) return;

    try {
      const token = await getToken();

      if (token) {
        const expiresDate = new Date();
        expiresDate.setTime(expiresDate.getTime() + 30 * 1000); // 30 seconds
        // Note: Base64 encoding is used here for cookie transport, not security.
        // The backend validates the actual Bearer token. Cookie has sameSite=strict
        // and short expiry (30s) to limit exposure. This matches Vue app behavior.
        Cookies.set('authorization', btoa(`Bearer ${token}`), {
          sameSite: 'strict',
          expires: expiresDate,
        });
      }

      // Create download objects from selected items
      const objects = selectedItems.map((item) => ({
        IsFolder: item.IsFolder,
        Path: item.Name,
      }));

      // Build URL params
      const urlParams = new URLSearchParams();
      urlParams.set('companyId', activeCompany.Id.toString());
      urlParams.set('currentFolderPath', getCurrentFolderPath());
      urlParams.set('objects', JSON.stringify(objects));

      // Build the download URL - use API base URL if set, otherwise relative
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const downloadPath = 'api/StorageDownload/Download';
      const downloadUrl = apiBaseUrl 
        ? `${apiBaseUrl}/${downloadPath}?${urlParams.toString()}`
        : `${downloadPath}?${urlParams.toString()}`;

      // Create and click download link
      const link = document.createElement('a');
      link.setAttribute('download', '');
      link.href = downloadUrl;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();

      addSnackbar(t('pages.main.snackbarDownloadStarting'), 'success');
    } catch (error) {
      addSnackbar(
        t('pages.main.snackbarDownloadFailed'),
        'error',
        error instanceof Error ? error.message : String(error),
        true
      );
    }
  }, [activeCompany, selectedItems, getCurrentFolderPath, addSnackbar, t]);

  // TODO: Re-enable preview once file type handling is improved.
  // Implementation should fetch the file via apiClient (which already
  // injects the unified auth bearer token) and open the resulting blob
  // in a new tab. See git history for the previous Okta-coupled draft.

  const handleCompanyMenuClose = () => {
    setCompanyMenuAnchor(null);
  };

  const handleCompanySelect = (company: Company) => {
    setActiveCompany(company);
    handleCompanyMenuClose();
  };

  return (
    <Box sx={{ pb: 2 }}>
      {/* Toolbar - Full width, flush with top nav */}
      <Paper
        data-testid="main-page-toolbar"
        sx={{
          p: 2,
          px: 3,
          bgcolor: '#1e3a5f',
          borderRadius: 0,
          position: 'fixed',
          top: 64,
          left: 0,
          right: 0,
          zIndex: 1099,
          boxShadow: 2,
        }}
        elevation={0}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {/* ── Zone 1: File Operations ── */}
          <Button
            data-testid="main-page-upload-button"
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadOpen(true)}
            sx={{
              bgcolor: 'white',
              color: 'primary.dark',
              '&:hover': { bgcolor: 'grey.200' },
            }}
          >
            {t('pages.main.upload')}
          </Button>

          <Button
            data-testid="main-page-file-actions-button"
            variant="contained"
            endIcon={<ArrowDropDown />}
            onClick={(e) => setFileActionsAnchor(e.currentTarget)}
            aria-haspopup="true"
            aria-expanded={fileActionsOpen}
            aria-controls={fileActionsOpen ? 'main-page-file-actions-menu' : undefined}
            sx={{
              bgcolor: 'white',
              color: 'primary.dark',
              '&:hover': { bgcolor: 'grey.200' },
            }}
          >
            {t('pages.main.fileActions')}
          </Button>
          <Menu
            id="main-page-file-actions-menu"
            data-testid="main-page-file-actions-menu"
            anchorEl={fileActionsAnchor}
            open={fileActionsOpen}
            onClose={() => setFileActionsAnchor(null)}
          >
            <MenuItem
              data-testid="main-page-create-folder-button"
              onClick={() => { setCreateFolderOpen(true); setFileActionsAnchor(null); }}
            >
              <ListItemIcon><CreateNewFolder fontSize="small" /></ListItemIcon>
              <ListItemText>{t('pages.main.createFolder')}</ListItemText>
            </MenuItem>
            <MenuItem
              data-testid="main-page-download-button"
              onClick={() => { handleDownload(); setFileActionsAnchor(null); }}
              disabled={!hasSelection}
            >
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('pages.main.download')}</ListItemText>
            </MenuItem>
            <MenuItem
              data-testid="main-page-delete-button"
              onClick={() => { setDeleteOpen(true); setFileActionsAnchor(null); }}
              disabled={!hasSelection}
              sx={{ color: 'error.main', '&.Mui-disabled': { color: 'action.disabled' } }}
            >
              <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: hasSelection ? 'error.main' : 'action.disabled' }} /></ListItemIcon>
              <ListItemText>{t('pages.main.deleteFiles')}</ListItemText>
            </MenuItem>
          </Menu>

          <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'rgba(255,255,255,0.3)' }} />

          {/* ── Zone 2: Processing Operations ── */}
          <LoadControls />

          {showSubmissionReport && (
            <Button
              data-testid="main-page-submission-report-button"
              variant="text"
              startIcon={<DescriptionIcon />}
              onClick={() => setSubmissionReportOpen(true)}
              sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
            >
              Submission Report
            </Button>
          )}

          {showApplicationReport && (
            <Button
              data-testid="main-page-application-report-button"
              variant="text"
              startIcon={<DescriptionIcon />}
              onClick={() => setApplicationReportOpen(true)}
              sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
            >
              Application Report
            </Button>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title={activityTooltipLines} slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}>
            <Button
              data-testid="main-page-activity-button"
              color="inherit"
              onClick={() => setActivityDialogOpen(true)}
              startIcon={
                pendingCount > 0 ? (
                  <CircularProgress size={20} color="inherit" data-testid="main-page-activity-spinner" />
                ) : undefined
              }
              sx={{ color: 'white' }}
            >
              {pendingCount > 0 && failedCount > 0
                ? t('pages.main.activityActiveFailed', { active: pendingCount, failed: failedCount })
                : pendingCount > 0
                ? t('pages.main.activityActive', { active: pendingCount })
                : failedCount > 0
                ? t('pages.main.activityFailed', { failed: failedCount })
                : t('pages.main.activity')}
            </Button>
          </Tooltip>
        </Stack>
      </Paper>
      <Menu
        data-testid="main-page-company-menu"
        anchorEl={companyMenuAnchor}
        open={companyMenuOpen}
        onClose={handleCompanyMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {mockCompanies.map((company) => (
          <MenuItem
            key={company.Id}
            onClick={() => handleCompanySelect(company)}
            selected={activeCompany?.Id === company.Id}
          >
            <ListItemIcon>
              <BusinessIcon />
            </ListItemIcon>
            <ListItemText
              primary={company.Name}
              secondary={t('pages.main.companyId', { id: company.Id })}
            />
          </MenuItem>
        ))}
        {mockCompanies.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary={t('pages.main.noCompaniesAvailable')} />
          </MenuItem>
        )}
      </Menu>

      <CreateFolder open={createFolderOpen} onOpenChange={setCreateFolderOpen} />
      <Upload open={uploadOpen} onOpenChange={setUploadOpen} />
      <Rename
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        itemName={selectedItems[0]?.Name}
      />
      <BatchRenameDialog
        open={batchRenameOpen}
        items={selectedItems.map((item) => item.Name)}
        onClose={() => setBatchRenameOpen(false)}
      />
      <Delete open={deleteOpen} onClose={() => setDeleteOpen(false)} />

      <ActivityDialog />
      <ConfigurationDialog />

      <ReportDialog
        open={submissionReportOpen}
        onClose={() => setSubmissionReportOpen(false)}
        title="Submission Report"
        downloadUrl={submissionReportUrl}
        showRelativePaths
      />
      <ReportDialog
        open={applicationReportOpen}
        onClose={() => setApplicationReportOpen(false)}
        title="Application Report"
        downloadUrl={applicationReportUrl}
      />

      {/* Breadcrumbs and Search Bar Row */}
      <Box sx={{ 
        mt: '115px', 
        mb: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2
      }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label={t('breadcrumbs.breadcrumbLabel')} data-testid="main-page-breadcrumbs">
          {breadcrumbs.map((crumb, idx) => (
            idx === breadcrumbs.length - 1 ? (
              <Typography key={crumb.Path} color="text.primary" data-testid={`breadcrumb-item-${idx}`}>{crumb.Name}</Typography>
            ) : (
              <Link
                key={crumb.Path}
                color="inherit"
                underline="hover"
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  navigateToBreadcrumb(idx);
                  setRepoSearch('');
                }}
                data-testid={`breadcrumb-link-${idx}`}
              >
                {crumb.Name}
              </Link>
            )
          ))}
        </Breadcrumbs>

        {/* Search Bar */}
        <TextField
          data-testid="main-page-search-input"
          value={repoSearch}
          onChange={(e) => setRepoSearch(e.target.value)}
          placeholder={t('pages.main.searchCurrentFolder')}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 300, minWidth: 200 }}
        />
      </Box>

      {/* File/folder grid */}
      {/* TODO: Re-enable preview once file type handling is improved */}
      {/* <RepoGrid searchTerm={repoSearch} onPreviewFile={handlePreviewFile} /> */}
      <RepoGrid searchTerm={repoSearch} onClearSearch={() => setRepoSearch('')} />
    </Box>
  );
}

export default MainPage;